import { Editor, Extensions } from '@tiptap/react';
import { StarterKitOptions } from '@tiptap/starter-kit';
import clsx from 'clsx';
import React from 'react';

import { EditorAddLink } from '@/ui/Editor/components/EditorAddLink';
import {
	BlockquoteMenuItem,
	BoldMenuItem,
	BulletListMenuItem,
	FontSizeMenuItem,
	HeadingsMenuItem,
	HistoryMenuItem,
	HorizontalRuleMenuItem,
	ItalicMenuItem,
	OrderedListMenuItem,
	StrikeMenuItem,
} from '@/ui/Editor/components/MenuBar/items';

import { Divider } from '../Divider';
import { TextAlignItem } from './items/TextAlignItem';
import { AddImage } from '../AddImage';

interface IMenuBar {
	editor: Editor;
	textAlign?: boolean;
}

const MenuBar = ({ editor, textAlign }: IMenuBar) => {
	const extensions: Extensions = editor?.extensionManager?.extensions ?? [];

	const starterKit = extensions.find((item) => item.name === 'starterKit');

	const { bold, italic, strike, bulletList, orderedList, blockquote, horizontalRule } = (starterKit?.options ??
		{}) as StarterKitOptions;

	const headingsEnabled = !!extensions.find((item) => item.name === 'headings');
	const fontSizeEnabled = !!extensions.find((item) => item.name === 'font');
	const linkEnabled = !!extensions.find((item) => item.name === 'link');
	const historyEnabled = !!extensions.find((item) => item.name === 'history');
	const boldEnabled = bold !== false;
	const italicEnabled = italic !== false;
	const strikeEnabled = strike !== false;
	const bulletListEnabled = bulletList !== false;
	const orderedListEnabled = orderedList !== false;
	const blockquoteEnabled = blockquote !== false;
	const horizontalRuleEnabled = horizontalRule !== false;

	return (
		<div
			className={clsx('flex gap-2 py-2 px-3 rounded-t border border-gray-300')}
			style={{ backgroundColor: '#f7f7fa' }}
		>
			{(headingsEnabled || fontSizeEnabled) && (
				<div className="flex gap-2">
					{headingsEnabled && <HeadingsMenuItem editor={editor} />}
					{fontSizeEnabled && <FontSizeMenuItem editor={editor} />}
				</div>
			)}

			{(boldEnabled || italicEnabled || strikeEnabled) && (
				<>
					<Divider />
					<div className="flex gap-2 items-center">
						{boldEnabled && <BoldMenuItem editor={editor} />}
						{italicEnabled && <ItalicMenuItem editor={editor} />}
						{strikeEnabled && <StrikeMenuItem editor={editor} />}
					</div>
				</>
			)}			

			{textAlign && (
				<>
					<Divider />
					<div className="flex gap-2 items-center">
						<TextAlignItem editor={editor} align='left'/>			
						<TextAlignItem editor={editor} align='center'/>			
						<TextAlignItem editor={editor} align='right'/>				
					</div>
				</>
			)}

			{(bulletListEnabled || orderedListEnabled) && (
				<>
					<Divider />
					<div className="flex gap-2 items-center">
						{bulletListEnabled && <BulletListMenuItem editor={editor} />}
						{orderedListEnabled && <OrderedListMenuItem editor={editor} />}
					</div>
				</>
			)}

			{linkEnabled && (
				<>
					<Divider />
					<div className="flex gap-2 items-center">
						{linkEnabled && <EditorAddLink editor={editor} />}
						<AddImage editor={editor} />
					</div>
				</>
			)}

			<Divider />

			{historyEnabled && <HistoryMenuItem editor={editor} />}
			{blockquoteEnabled && <BlockquoteMenuItem editor={editor} />}
			{horizontalRuleEnabled && <HorizontalRuleMenuItem editor={editor} />}
		</div>
	);
};

export { MenuBar };
